import { EntitiesInGroupModel } from "../../contexts/MapContextState";

/** Screen-space rectangle, in the same CSS pixels `mapbox.project` returns. */
export interface FrameRect {
  readonly left: number;
  readonly top: number;
  readonly right: number;
  readonly bottom: number;
}

export interface CountWellsInFrameParams {
  readonly mapbox: mapboxgl.Map;
  /** The capture frame, in canvas-relative pixels. */
  readonly frame: FrameRect;
  /**
   * Every layer that paints this entity kind. Horizontal and vertical wells
   * are separate layers on separate vector sources (`hz.mvt` / `vert.mvt`), so
   * passing one of them reads every well on the other as absent.
   */
  readonly layerIds: readonly string[];
  /**
   * The map's own group → entities map (`entitiesInGroup` / `facilitiesInGroup`).
   * Used only for row identity — which legend group a painted entity belongs
   * to. Which entities are painted is the layers' own business (see below).
   * Counting per group rather than per color means two rows sharing a color
   * cannot merge, and a palette difference between the legend payload and the
   * painted color cannot silently zero a row.
   */
  readonly entitiesByGroup: Maybe<Record<string, EntitiesInGroupModel>>;
}

/** Leading "(19) " on a label, which some names carry and others do not. */
const LABEL_COUNT_PREFIX = /^\(\s*[\d,]+\s*\)\s*/;
const LABEL_COUNT_WITH_SEPARATOR = /^\(\s*\d{1,3}(,\d{3})+\s*\)/;

/**
 * Strips the count a label carries in front of its name, so one group is
 * recognizable whichever side it arrives from: the map's group titles carry the
 * prefix ("(19) Charlie Lake") while a legend item's `title` is the bare name
 * ("Charlie Lake"). Matching those two raw never hits, and the miss is silent.
 *
 * Deliberately narrower than `utils/removeCountFromTitle`'s `removeCount`: this
 * accepts a thousands separator and leaves a trailing "(n)" alone, since a
 * group name can legitimately end in parentheses.
 */
export const stripLabelCount = (label: Maybe<string>): Maybe<string> =>
  label ? label.replace(LABEL_COUNT_PREFIX, "").trim() : label;

/**
 * Rewrites the count a label carries in front of its name — the map service
 * ships it inside `groupTitle`, and that string is what the row renders, so a
 * substituted `count` alone never shows. A label with no count is unchanged.
 */
export const replaceLabelCount = (label: Maybe<string>, count: number): Maybe<string> => {
  if (!label) {
    return label;
  }
  // The label is the only rendered number on a screenshot row, so the new count
  // is written the way the old one was: with a thousands separator only if the
  // live label carried one. A frame count never has more digits than the live
  // count it replaces, so a separator-free label stays separator-free.
  const usesThousandsSeparator = LABEL_COUNT_WITH_SEPARATOR.test(label);
  const formatted = usesThousandsSeparator
    ? count.toLocaleString("en-US")
    : String(count);
  return label.replace(LABEL_COUNT_PREFIX, `(${formatted}) `);
};

const HIDDEN_VISIBILITY = "none";

interface EntityIndex {
  /** uwi → normalized group title, for every entity the map is painting. */
  readonly groupByEntity: ReadonlyMap<string, string>;
  /** Every group's normalized title, so a group with nothing in the frame reports zero. */
  readonly groupTitles: readonly string[];
}

const EMPTY_INDEX: EntityIndex = { groupByEntity: new Map(), groupTitles: [] };

/**
 * Cached per `entitiesByGroup` object: that map holds every painted entity
 * (up to the entities-on-map cap) and only changes on a refilter, while the
 * count runs on every `idle` and on every tick of a frame-edge drag. Rebuilding
 * a six-figure index on each of those is the one avoidable cost here.
 */
const entityIndexCache = new WeakMap<object, EntityIndex>();

const indexEntities = (
  entitiesByGroup: Maybe<Record<string, EntitiesInGroupModel>>
): EntityIndex => {
  if (!entitiesByGroup) {
    return EMPTY_INDEX;
  }
  const cached = entityIndexCache.get(entitiesByGroup);
  if (cached) {
    return cached;
  }
  const groupByEntity = new Map<string, string>();
  const groupTitles: string[] = [];
  // Keyed on the normalized title, so two groups that normalize to the same
  // string merge into one tally — the same door the group-keyed design closes
  // for colors. Group titles are unique within a legend, so this needs the map
  // service to emit two rows differing only in their count prefix; noted rather
  // than guarded.
  Object.entries(entitiesByGroup).forEach(([groupKey, entities]) => {
    const groupTitle =
      stripLabelCount(entities?.group?.title) || stripLabelCount(groupKey) || groupKey;
    groupTitles.push(groupTitle);
    Object.keys(entities?.uwis ?? {}).forEach((uwi) => {
      groupByEntity.set(uwi, groupTitle);
    });
  });
  const index = { groupByEntity, groupTitles };
  entityIndexCache.set(entitiesByGroup, index);
  return index;
};

/** The fields of a style layer the counter reads; `AnyLayer` does not carry them on every variant. */
interface PaintingLayer {
  readonly source?: string;
  readonly minzoom?: number;
  readonly maxzoom?: number;
}

/** The one place `getLayer`'s return is narrowed to the fields the counter reads. */
const getPaintingLayer = (mapbox: mapboxgl.Map, layerId: string): Maybe<PaintingLayer> =>
  mapbox.getLayer?.(layerId) as Maybe<PaintingLayer>;

/**
 * Whether the layer's own source has finished loading. `areTilesLoaded()` is
 * style-wide — a slow base-map, shapefile or IPDB tile would make the frame
 * uncountable for as long as it hangs, with the legend silently on whole-map
 * counts — so only the sources actually being counted are asked.
 */
const isLayerSourceLoaded = (mapbox: mapboxgl.Map, layerId: string): boolean => {
  const source = getPaintingLayer(mapbox, layerId)?.source;
  return Boolean(source) && mapbox.isSourceLoaded(source);
};

/**
 * Whether the layer is drawing anything right now: present in the style, not
 * hidden, and inside its zoom range. The timeline animation hides the well
 * layer without removing it, and below the MVT/raster crossover the well layers
 * sit outside their zoom range — either way the layer is on the map and draws
 * nothing, so it must not be treated as a source of counts.
 */
const isLayerDrawing = (mapbox: mapboxgl.Map, layerId: string): boolean => {
  const layer = getPaintingLayer(mapbox, layerId);
  if (!layer) {
    return false;
  }
  if (mapbox.getLayoutProperty(layerId, "visibility") === HIDDEN_VISIBILITY) {
    return false;
  }
  const zoom = mapbox.getZoom();
  const aboveMinimum = layer.minzoom === undefined || zoom >= layer.minzoom;
  const belowMaximum = layer.maxzoom === undefined || zoom < layer.maxzoom;
  return aboveMinimum && belowMaximum;
};

/**
 * Value equality for two frame counts. The counter builds a fresh object per
 * run, and recounts now arrive per tile-load burst rather than per `idle`;
 * handing back the equal previous object lets React bail out instead of
 * re-rendering the legend for an unchanged number.
 */
export const areFrameCountsEqual = (
  a: Nullable<Record<string, number>>,
  b: Nullable<Record<string, number>>
): boolean => {
  if (a === b) {
    return true;
  }
  if (!a || !b) {
    return false;
  }
  const aGroups = Object.keys(a);
  return (
    aGroups.length === Object.keys(b).length &&
    aGroups.every((group) => a[group] === b[group])
  );
};

/**
 * Whether this source feeds one of the counted layers — the filter that lets a
 * `sourcedata` listener react to the well tiles landing while ignoring the base
 * map, grids and every other source the style is still loading.
 */
export const isCountedSource = (
  mapbox: mapboxgl.Map,
  layerIds: readonly string[],
  sourceId: Maybe<string>
): boolean =>
  Boolean(sourceId) &&
  layerIds.some((layerId) => getPaintingLayer(mapbox, layerId)?.source === sourceId);

/**
 * Whether the frame cannot be counted *right now* but will be shortly: a layer
 * is drawing and one of the sources being counted is still loading. This is
 * the transient after every refilter — the viewport widens on entry or a pan
 * lands, the well set changes, and its tiles reload — and it is distinct from
 * "cannot count at all" (nothing drawing, no groups). The caller holds its last
 * frame counts through the former; falling back to whole-map counts for a
 * second on every pan would print the one thing the frame legend exists to
 * avoid.
 */
export const isFrameCountPending = (
  mapbox: mapboxgl.Map,
  layerIds: readonly string[],
  entitiesByGroup: Maybe<Record<string, EntitiesInGroupModel>>
): boolean => {
  // No groups is "cannot count", not "about to": the map has stopped saying
  // what it paints (group mode, or past the entities-on-map cap), and no tile
  // load will change that. Holding here would pin counts from a well set the
  // map no longer shows.
  if (indexEntities(entitiesByGroup).groupByEntity.size === 0) {
    return false;
  }
  const drawingLayerIds = layerIds.filter((layerId) => isLayerDrawing(mapbox, layerId));
  return (
    drawingLayerIds.length > 0 &&
    drawingLayerIds.some((layerId) => !isLayerSourceLoaded(mapbox, layerId))
  );
};

/**
 * Counts, per legend group, the entities the map is drawing inside the capture
 * frame.
 *
 * Counted from the rendered map rather than by asking the filter service for a
 * frame-scoped filter: that service stores a filter's well list against the
 * session, so a second filter created while the live one is in use overwrites
 * it and the map repaints to the smaller set. Counting here also means the
 * numbers follow a pan immediately, and nothing re-derives — rows, colors and
 * bin edges all stay the live legend's, so a quantile legend is as safe as any
 * other.
 *
 * `queryRenderedFeatures` over the frame does the geometric work: it is
 * restricted to the frame rather than every loaded tile, it honors each
 * layer's own filter, visibility and zoom range (so the counted set *is* the
 * painted set, extra filter clauses included), and it catches a horizontal
 * crossing the frame with both ends outside. Its hit test includes the drawn
 * line width, so a well passing within a stroke of the frame edge counts —
 * which is also what the image shows.
 *
 * A Pad Generator leg carries its parent well's `Uwi`, so a leg crossing the
 * frame counts its parent even when the parent's own lateral lies outside: part
 * of that well's geometry is in the image, and the dedupe means it counts once.
 *
 * Keyed by group name with any count prefix stripped, since the map's group
 * titles carry one and legend item titles do not. Every known group is present
 * in the result, zero included, so a caller can tell "no entities in the frame"
 * from "this row is not a group the map knows". `null` means the frame could
 * not be counted at all.
 */
export const countWellsInFrame = ({
  mapbox,
  frame,
  layerIds,
  entitiesByGroup
}: CountWellsInFrameParams): Nullable<Record<string, number>> => {
  const { groupByEntity, groupTitles } = indexEntities(entitiesByGroup);

  // Nothing painted yet: the map has not told us which entities are on it, so
  // there is nothing to count against.
  if (groupByEntity.size === 0) {
    return null;
  }
  const countsByGroup: Record<string, number> = {};
  groupTitles.forEach((groupTitle) => {
    countsByGroup[groupTitle] = 0;
  });

  // Asking for a layer the style does not have logs a mapbox error and yields
  // nothing, and a layer that is drawing nothing would read every group as
  // empty. With no layer drawing, the frame cannot be counted: reporting zeroes
  // would rewrite every row to (0) and the zero-count filter would then drop
  // the whole legend off the screenshot. Null hands the live counts back.
  const drawingLayerIds = layerIds.filter((layerId) => isLayerDrawing(mapbox, layerId));
  if (drawingLayerIds.length === 0) {
    return null;
  }

  // Tiles for the resized viewport arrive a moment after the frame appears;
  // until they have, what is rendered is not yet what will be. `idle` follows
  // once they land and the recount then reads the settled map. The caller can
  // tell this null from the others through `isFrameCountPending`.
  const everySourceLoaded = drawingLayerIds.every((layerId) =>
    isLayerSourceLoaded(mapbox, layerId)
  );
  if (!everySourceLoaded) {
    return null;
  }

  const renderedFeatures = mapbox.queryRenderedFeatures(
    [
      [frame.left, frame.top],
      [frame.right, frame.bottom]
    ],
    { layers: drawingLayerIds }
  );

  // One entity arrives as many tile features, and can sit on more than one
  // layer; count each one once.
  const counted = new Set<string>();
  renderedFeatures.forEach((feature) => {
    const uwi = feature.properties?.Uwi;
    if (!uwi || counted.has(uwi)) {
      return;
    }
    // Marked before the group lookup: an entity the map is not grouping is the
    // one that arrives as the most repeat tile features, and each repeat would
    // otherwise re-run the lookup.
    counted.add(uwi);
    const groupTitle = groupByEntity.get(uwi);
    if (groupTitle === undefined) {
      return;
    }
    countsByGroup[groupTitle] += 1;
  });

  return countsByGroup;
};
